/**
 * Os passos da linha de raciocínio, prontos para a lista (11/09/2026).
 *
 * `agruparPassos(passos, { auxiliares, passado })` → `[{ chave, rotulo, verbo,
 * complemento, vezes, meta }]`:
 *
 * - **repetidos consecutivos viram UMA linha** (`vezes: 15` — a tela mostra
 *   `Consultando cliente... · 15×`): um turno que consulta 15 clientes emitia
 *   15 linhas iguais;
 * - **em passado depois do `end`** (`passado: true`): o rótulo da tool vira
 *   `TOOL_LABELS_PASSADO` ("Consultei o cliente"); tool sem entrada cai no
 *   rótulo presente sem as reticências; passo que não é tool (o racional da
 *   triage) fica como está;
 * - **verbo × complemento** (14/09/2026): o rótulo de tool é sempre "VERBO +
 *   objeto" ("Consultei" + "o funil de leads"), e a tela pinta os dois em tons
 *   diferentes — o verbo apagado, o objeto escuro. Separar aqui a primeira
 *   palavra custa nada e poupa mexer nos 100+ rótulos de `TextChat.js`. Só
 *   para passo DE TOOL: o racional da triage é frase livre e a primeira
 *   palavra não é verbo ("Pessoas conduz…") — vai inteiro no `complemento`;
 * - **meta** (`auxiliares[rotulo]`, ex.: "1.667 linhas", mais o `15×` dos
 *   repetidos, já unidos por ` · `): o que o preview de artefato já calcula
 *   para a parte correspondente — a bolha monta o mapa rótulo → contagem, e
 *   aqui só se anexa. O template imprime `· {{ meta }}` sem montar nada.
 *
 * Função pura. Sem `?.`/`??` (o Babel deste projeto não transpila).
 */
import { TOOL_LABELS } from '@modules/aawz-intelligence-v2/constants/Text'
import { RACIOCINIO_LINHA, TOOL_LABELS_PASSADO } from '@modules/aawz-intelligence-v2/constants/TextChat'

const RETICENCIAS = /(\.\.\.|…)\s*$/
// Primeira palavra + o resto, separados pelo primeiro espaço.
const VERBO_E_COMPLEMENTO = /^(\S+)\s+([\s\S]+)$/
const SEPARADOR_DA_META = ' · '

// rótulo presente → nome da tool (invertido de `TOOL_LABELS`, uma vez).
const TOOL_POR_ROTULO = Object.keys(TOOL_LABELS).reduce(function (mapa, nome) {
  mapa[TOOL_LABELS[nome]] = nome
  return mapa
}, {})

/** O passo é uma chamada de tool conhecida? */
export function ehPassoDeTool(passo) {
  return Object.prototype.hasOwnProperty.call(TOOL_POR_ROTULO, passo)
}

/** O rótulo em passado — ou o presente sem reticências, ou o passo intacto. */
export function rotuloEmPassado(passo) {
  const tool = TOOL_POR_ROTULO[passo]
  if (tool && TOOL_LABELS_PASSADO[tool]) return TOOL_LABELS_PASSADO[tool]
  if (tool) return passo.replace(RETICENCIAS, '')
  return passo
}

/**
 * `{ verbo, complemento }` do rótulo: a primeira palavra e o resto. Rótulo de
 * uma palavra só ("Testando...") é só verbo; passo que não é tool é só
 * complemento (não se adivinha verbo em frase livre).
 */
export function separarVerbo(rotulo, ehTool) {
  if (!ehTool) return { verbo: '', complemento: rotulo }
  const partes = VERBO_E_COMPLEMENTO.exec(rotulo)
  if (!partes) return { verbo: rotulo, complemento: '' }
  return { verbo: partes[1], complemento: partes[2] }
}

/** O sufixo muted da linha: `15×` dos repetidos e/ou o auxiliar, unidos por ` · `. */
function metaDoPasso(vezes, auxiliar) {
  const partes = []
  if (vezes > 1) partes.push(RACIOCINIO_LINHA.vezes(vezes))
  if (auxiliar) partes.push(auxiliar)
  return partes.join(SEPARADOR_DA_META)
}

/**
 * @param {string[]} passos
 * @param {Object=}  opcoes
 * @param {Object=}  opcoes.auxiliares  `{ [rotuloPresente]: 'texto' }`.
 * @param {boolean=} opcoes.passado     O turno acabou: rótulos em passado.
 * @returns {Array<{chave: string, rotulo: string, verbo: string, complemento: string, vezes: number, meta: string}>}
 */
export function agruparPassos(passos, opcoes) {
  const config = opcoes || {}
  const auxiliares = config.auxiliares || {}
  const grupos = []
  ;(passos || []).forEach(function (passo) {
    if (!passo) return
    const ultimo = grupos[grupos.length - 1]
    if (ultimo && ultimo.original === passo) {
      ultimo.vezes += 1
      return
    }
    grupos.push({ original: passo, vezes: 1 })
  })
  return grupos.map(function (grupo, indice) {
    const rotulo = config.passado ? rotuloEmPassado(grupo.original) : grupo.original
    const partes = separarVerbo(rotulo, ehPassoDeTool(grupo.original))
    return {
      chave: indice + '-' + grupo.original,
      rotulo: rotulo,
      verbo: partes.verbo,
      complemento: partes.complemento,
      vezes: grupo.vezes,
      meta: metaDoPasso(grupo.vezes, auxiliares[grupo.original] || ''),
    }
  })
}
